import React, { useEffect, useState } from 'react';

import { Button, Popover, TextFieldProps } from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import { useDebounce } from 'hooks/useDebounce';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { DateObject } from 'react-multi-date-picker';
import { Calendar } from 'shared/mui/Calendar/Calendar';
import { DatePickerActions } from 'shared/mui/DatePicker/components/DatePickerActions/DatePickerActions';
import { DatePickerMaskInput } from 'shared/mui/DatePicker/components/DatePickerMaskInput/DatePickerMaskInput';
import {
  DEFAULT_SHORTCUTS,
  Shortcuts,
} from 'shared/mui/DatePicker/components/DatePickerShortcuts/constants/shortcuts';
import { DatePickerShortcuts } from 'shared/mui/DatePicker/components/DatePickerShortcuts/DatePickerShortcuts';
import { DatePickerToolbar } from 'shared/mui/DatePicker/components/DatePickerToolbar/DatePickerToolbar';
import {
  DEFAULT_DATE_MASK,
  DEFAULT_DATE_RANGE_MASK,
  DEFAULT_DATE_RANGE_PLACEHOLDER,
  DEFAULT_SEPARATOR,
} from 'shared/mui/DatePicker/config/defaultValues';
import {
  PopoverCalendarContainerStyled,
  PopoverContentStyled,
  PopoverDatesStyled,
  PopoverLayoutStyled,
  PopoverListStyled,
} from 'shared/mui/DatePicker/DatePicker.styled';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { formatRangeInputToPlaceholder } from 'shared/mui/DatePicker/utils/formatRangeInputToPlaceholder';
import { formatInputToTimestamp } from 'utils/date';

export type DateRangePickerValue = number | null;

export type DateRangePickerProps = Omit<TextFieldProps, 'value' | 'onChange' | 'defaultValue'> & {
  value: [DateRangePickerValue, DateRangePickerValue];
  numberOfMonths?: 1 | 2;
  displayShortcuts?: boolean;
  onChange?: (newValue: [DateRangePickerValue, DateRangePickerValue]) => void;
  maxDate?: Date;
  shortcuts?: Shortcuts;
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  variant = 'outlined',
  numberOfMonths = 2,
  displayShortcuts = true,
  maxDate,
  disabled,
  onChange: onChangeProp,
  value,
  shortcuts = (timezone) => [DEFAULT_SHORTCUTS(timezone)[0]],
  size = 'small',
  ...restProps
}) => {
  const translate = useTypedTranslate();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const [error, setError] = useState(false);

  const [resetValue, setResetValue] = useState<[DateRangePickerValue, DateRangePickerValue]>(value);

  const [inputValue, setInputValue] = useState<string | undefined>();

  const [calendarValue, setCalendarValue] =
    useState<[DateRangePickerValue, DateRangePickerValue]>(value);

  const open = Boolean(anchorEl);
  const id = open ? 'date-picker-popover' : undefined;

  const debouncedOnChangeInputHandler = useDebounce((target: string) => {
    const [start, end] = target.split(DEFAULT_SEPARATOR);

    setError(true);
    const validStart = formatInputToTimestamp(start, { format: DEFAULT_DATE_MASK });
    const validEnd = formatInputToTimestamp(end, { format: DEFAULT_DATE_MASK });

    if (validStart) {
      setError(false);
      setCalendarValue((prev) => [validStart, prev[1]]);
    } else {
      setCalendarValue((prev) => [null, prev[1]]);
    }

    if (validEnd) {
      setError(false);
      setCalendarValue((prev) => [prev[0], validEnd]);
    } else {
      setCalendarValue((prev) => [prev[0], null]);
    }

    setInputValue(target);
  }, 300);

  function onChangeInput(event: React.ChangeEvent<HTMLInputElement>) {
    debouncedOnChangeInputHandler(event?.target?.value);
  }

  function onChangeCalendar(value: (DateObject | null)[]) {
    const [start, end] = value;

    const validStart = start ? Number(startOfDay(start.valueOf())) : null;
    const validEnd = end ? Number(endOfDay(end.valueOf())) : null;

    const validValue: [DateRangePickerValue, DateRangePickerValue] = [validStart, validEnd];

    setInputValue(formatRangeInputToPlaceholder(validValue));
    setCalendarValue(validValue);
  }

  const onClickShortcut = (value: [DateRangePickerValue, DateRangePickerValue]) => {
    setInputValue(formatRangeInputToPlaceholder(value));
    setCalendarValue(value);
  };

  function onClickApply() {
    if (onChangeProp) {
      const validValue: [DateRangePickerValue, DateRangePickerValue] = [
        calendarValue[0],
        calendarValue[1] || Number(endOfDay(new Date())),
      ];
      setCalendarValue(validValue);
      onChangeProp(validValue);
      setResetValue(validValue);
      handleClose();
    }
  }

  function onClickReset() {
    if (onChangeProp) {
      onChangeProp(resetValue);
      setInputValue(formatRangeInputToPlaceholder(resetValue));
      setCalendarValue(resetValue);
      handleClose();
    }
  }

  const onResetInput = () => {
    if (onChangeProp) {
      setInputValue('');
      setCalendarValue([null, null]);
      onChangeProp([null, null]);
      setResetValue([null, null]);
      setError(false);
    }
  };

  useEffect(() => {
    setResetValue(value);

    if (value[0] || value[1]) {
      setCalendarValue(value);
      setInputValue(
        formatRangeInputToPlaceholder([value[0], value[1]], {
          format: DEFAULT_DATE_MASK,
        }),
      );
      return;
    }
    setCalendarValue([null, null]);
    setInputValue('');
  }, [value]);

  return (
    <>
      <DatePickerMaskInput
        {...restProps}
        disabled={disabled}
        error={error}
        label={label}
        mask={DEFAULT_DATE_RANGE_MASK}
        onChange={onChangeInput}
        onClick={(e) => {
          if (disabled) {
            return;
          }

          handleClick(e);
        }}
        onResetInput={onResetInput}
        placeholder={DEFAULT_DATE_RANGE_PLACEHOLDER}
        size={size}
        value={inputValue}
        variant={variant}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disableAutoFocus={true}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <PopoverLayoutStyled
          sx={{
            width: '713px',
          }}
        >
          <PopoverCalendarContainerStyled>
            {displayShortcuts && (
              <PopoverListStyled>
                <DatePickerShortcuts onClick={onClickShortcut} shortcuts={shortcuts} />
              </PopoverListStyled>
            )}
            <PopoverContentStyled>
              <DatePickerToolbar end={calendarValue[1]} start={calendarValue[0]} />
              <PopoverDatesStyled>
                <PopoverCalendarContainerStyled>
                  <Calendar
                    maxDate={maxDate}
                    numberOfMonths={numberOfMonths}
                    onChange={onChangeCalendar}
                    range
                    value={calendarValue}
                  />
                </PopoverCalendarContainerStyled>
              </PopoverDatesStyled>
            </PopoverContentStyled>
          </PopoverCalendarContainerStyled>
          <DatePickerActions>
            <Button onClick={onClickReset}>{translate('app.buttons.cancel')}</Button>
            <Button onClick={onClickApply} variant="contained">
              {translate('app.buttons.confirm')}
            </Button>
          </DatePickerActions>
        </PopoverLayoutStyled>
      </Popover>
    </>
  );
};
